import { useTranslation } from "react-i18next";
import { Badge, type badgeVariants } from "~/atoms/badge";
import type { VariantProps } from "class-variance-authority";
import {
  DocumentStatus,
  DocumentArtifactStatus,
  UserStatus,
} from "~/config/types";

type BadgeVariant = VariantProps<typeof badgeVariants>["variant"];

const documentStatusVariant: Record<DocumentStatus, BadgeVariant> = {
  [DocumentStatus.active]: "success",
  [DocumentStatus.archived]: "destructive",
};

const documentArtifactStatusVariant: Record<
  DocumentArtifactStatus,
  BadgeVariant
> = {
  [DocumentArtifactStatus.draft]: "secondary",
  [DocumentArtifactStatus.published]: "success",
  [DocumentArtifactStatus.archived]: "destructive",
};

const userStatusVariant: Record<UserStatus, BadgeVariant> = {
  [UserStatus.active]: "success",
  [UserStatus.deactivated]: "secondary",
  [UserStatus.pending]: "warn",
  [UserStatus.deleted]: "destructive",
};

const DocumentStatusBadge = ({ status }: { status: DocumentStatus }) => {
  const { t } = useTranslation();

  const label: Record<DocumentStatus, string> = {
    [DocumentStatus.active]: t(($) => $.commons.document.statuses.active),
    [DocumentStatus.archived]: t(($) => $.commons.document.statuses.archived),
  };

  return <Badge variant={documentStatusVariant[status]}>{label[status]}</Badge>;
};

const DocumentArtifactStatusBadge = ({
  status,
}: {
  status: DocumentArtifactStatus;
}) => {
  const { t } = useTranslation();

  const label: Record<DocumentArtifactStatus, string> = {
    [DocumentArtifactStatus.draft]: t(($) => $.commons.document.statuses.draft),
    [DocumentArtifactStatus.published]: t(
      ($) => $.commons.document.statuses.published,
    ),
    [DocumentArtifactStatus.archived]: t(
      ($) => $.commons.document.statuses.archived,
    ),
  };

  return (
    <Badge variant={documentArtifactStatusVariant[status]}>
      {label[status]}
    </Badge>
  );
};

const UserStatusBadge = ({ status }: { status: UserStatus }) => {
  const { t } = useTranslation();

  const label: Record<UserStatus, string> = {
    [UserStatus.active]: t(($) => $.commons.user.statuses.active),
    [UserStatus.deactivated]: t(($) => $.commons.user.statuses.deactivated),
    [UserStatus.pending]: t(($) => $.commons.user.statuses.pending),
    [UserStatus.deleted]: t(($) => $.commons.user.statuses.deleted),
  };

  return <Badge variant={userStatusVariant[status]}>{label[status]}</Badge>;
};

export const StatusBadge = {
  DocumentStatus: DocumentStatusBadge,
  DocumentArtifactStatus: DocumentArtifactStatusBadge,
  UserStatus: UserStatusBadge,
};
